/**
 * Modal.tsx
 * Composant modal réutilisable avec Tailwind CSS
 * Version unifiée pour le design system
 */
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '../utils/cn';

// A25 : sélecteur des éléments focusables pour le piège de focus.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
  className?: string;
  showCloseButton?: boolean;
}

const sizeClasses = {
  small: 'w-full max-w-md',
  medium: 'w-full max-w-lg',
  large: 'w-full max-w-2xl',
  fullscreen: 'w-[95vw] h-[95vh]',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  className = '',
  showCloseButton = true,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Fermer avec Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // A25 : gestion du focus (accessibilité, WCAG 2.4.3 / 2.1.2).
  // - mémorise l'élément déclencheur et lui rend le focus à la fermeture ;
  // - place le focus sur le 1er élément focusable à l'ouverture ;
  // - piège le focus (Tab/Shift+Tab) à l'intérieur de la modale.
  // Bénéficie à TOUTES les modales (ce composant est la base partagée).
  useEffect(() => {
    if (!isOpen) return;
    // Mémoriser le déclencheur (sauf si c'est déjà le body — évite de « restaurer »
    // vers nulle part, notamment avec le double-montage de React StrictMode).
    const trigger = document.activeElement as HTMLElement | null;
    if (trigger && trigger !== document.body) {
      previouslyFocused.current = trigger;
    }

    const focusFirst = () => {
      const node = dialogRef.current;
      if (!node) return;
      const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) {
        node.focus();
        return;
      }
      // Préférer le 1er champ de saisie s'il existe (ex. PromptModal), sinon le
      // 1er focusable qui n'est pas le bouton de fermeture (X).
      const input = focusables.find(el => el.tagName === 'INPUT' || el.tagName === 'TEXTAREA');
      const nonClose = focusables.find(el => el.getAttribute('aria-label') !== 'Fermer');
      (input ?? nonClose ?? focusables[0]).focus();
    };
    // setTimeout(0) après le commit : plus fiable que rAF face au double-montage
    // StrictMode (le rAF de la 1re passe peut être annulé avant de s'exécuter).
    const focusTimer = setTimeout(focusFirst, 0);

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const node = dialogRef.current;
      if (!node) return;
      const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        el => el.offsetParent !== null
      );
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !node.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !node.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleTab);
      // Restaurer le focus au déclencheur, seulement s'il est toujours dans le DOM
      // (évite de focaliser un élément démonté / le body avec StrictMode).
      const prev = previouslyFocused.current;
      if (prev && document.contains(prev)) {
        prev.focus?.();
      }
    };
  }, [isOpen]);

  // Empêcher le scroll du body quand la modal est ouverte
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1050] bg-black/50 flex items-center justify-center animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cn(
          'bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh] outline-none',
          'animate-in slide-in-from-bottom-4 duration-200',
          sizeClasses[size],
          className
        )}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="modal-title" className="text-xl font-semibold text-gray-900 m-0">
            {title}
          </h2>
          {showCloseButton && (
            <button
              className="p-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
              onClick={onClose}
              aria-label="Fermer"
              type="button"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
};
